import type { BillData, HelioScoreInput } from '@/api/types';

const DEFAULT_ROOF_AZIMUTH = 180;
const DEFAULT_ROOF_TILT = 35;
export const DEFAULT_IMPORT_TARIFF_PENCE = 28.62;
export const DEFAULT_ANNUAL_KWH = 3500;
const SELF_USE_RATE_CURVE_FLOOR = 0.45;
const SELF_USE_RATE_CURVE_CAP = 0.95;
const SELF_USE_RATE_CURVE_K = 1.79;
const SELF_USE_RATE_CURVE_IRRADIANCE = 950;
const INDICATIVE_SYSTEM_KW_PER_M2 = 0.18;

export interface HelioScoreFunnelSolarInsights {
  roofAreaM2?: number;
  roofOrientation?: string;
  azimuthDegrees?: number;
  maxSunshineHoursPerYear?: number;
  shadingFactor?: number;
}

export interface HelioScoreFunnelFormSlice {
  postcode: string;
  address: string;
  annualKwh: number;
  billData: BillData | null;
  /** True once the user has manually edited usage on the funnel (not from bill defaults). */
  usageTouched?: boolean;
  solarInsights: HelioScoreFunnelSolarInsights | null;
  solarData: {
    roofAreaM2: number;
    irradianceKwhM2: number;
  } | null;
  selectedTier: {
    systemKw: number;
    batteryKwh: number;
    includesEv: boolean;
    price?: number;
    paybackYears?: number;
    annualSavings?: number;
  } | null;
  /** Populated after address confirm enrichment (KAN-142). */
  epcEnrichment?: EpcEvidenceSlice | null;
}

export interface EpcEvidenceSlice {
  epc_rating?: string | null;
  epc_floor_area_m2?: number | null;
  property_tenure?: string | null;
  property_built_form?: string | null;
  property_value_mid_gbp?: number | null;
  epc_data_source?: string | null;
  price_paid_data_source?: string | null;
  evidence_flags?: {
    epc_from_epc_register?: boolean;
    property_value_from_price_paid?: boolean;
    tenure_from_epc_register?: boolean;
  };
}

export interface HelioScoreFunnelContext {
  /** Funnel step index (1 = Analysis … 3 = Account). */
  step: number;
  heroIrradianceKwhM2?: number;
}

function parseRoofAzimuth(orientation?: string): number {
  if (!orientation?.trim()) {
    return DEFAULT_ROOF_AZIMUTH;
  }

  const value = orientation.trim().toLowerCase();
  if (value.includes('south') && value.includes('east')) return 135;
  if (value.includes('south') && value.includes('west')) return 225;
  if (value.includes('north') && value.includes('east')) return 45;
  if (value.includes('north') && value.includes('west')) return 315;
  if (value.includes('south')) return 180;
  if (value.includes('east')) return 90;
  if (value.includes('west')) return 270;
  if (value.includes('north')) return 0;

  const numeric = Number.parseFloat(orientation);
  return Number.isFinite(numeric) ? numeric : DEFAULT_ROOF_AZIMUTH;
}

/**
 * Google Solar API shading_factor is sun retention (1.0 = no shading).
 * Helio Score expects shading_loss (0.0 = no shading).
 */
export function shadingFactorToLoss(shadingFactor?: number): number | undefined {
  if (shadingFactor == null || !Number.isFinite(shadingFactor) || shadingFactor <= 0) {
    return undefined;
  }

  const retention = Math.max(0, Math.min(1, shadingFactor));
  return Math.max(0, Math.min(1, 1 - retention));
}

export function resolveRoofAzimuth(insights?: HelioScoreFunnelSolarInsights | null): number {
  if (insights?.azimuthDegrees != null && Number.isFinite(insights.azimuthDegrees)) {
    let azimuth = insights.azimuthDegrees % 360;
    if (azimuth < 0) azimuth += 360;
    return azimuth;
  }

  return parseRoofAzimuth(insights?.roofOrientation);
}

export function resolveIrradianceKwhPerKwp(
  insights?: HelioScoreFunnelSolarInsights | null,
  solarData?: HelioScoreFunnelFormSlice['solarData'],
  heroIrradianceKwhM2?: number,
): number | undefined {
  const geospatial =
    insights?.maxSunshineHoursPerYear != null && insights.maxSunshineHoursPerYear > 0
      ? insights.maxSunshineHoursPerYear
      : undefined;

  if (geospatial != null) {
    return geospatial;
  }

  if (solarData?.irradianceKwhM2 != null && solarData.irradianceKwhM2 > 0) {
    return solarData.irradianceKwhM2;
  }

  if (heroIrradianceKwhM2 != null && heroIrradianceKwhM2 > 0) {
    return heroIrradianceKwhM2;
  }

  return undefined;
}

export function buildPropertyEvidenceFlags(
  form: HelioScoreFunnelFormSlice,
  insights?: HelioScoreFunnelSolarInsights | null,
): Record<string, boolean> {
  const flags: Record<string, boolean> = {};

  if (insights?.maxSunshineHoursPerYear != null && insights.maxSunshineHoursPerYear > 0) {
    flags.irradiance_from_geospatial = true;
  }
  if (insights?.azimuthDegrees != null && Number.isFinite(insights.azimuthDegrees)) {
    flags.roof_azimuth_from_geospatial = true;
  }
  if (
    (insights?.roofAreaM2 != null && insights.roofAreaM2 > 0)
    || (form.solarData?.roofAreaM2 != null && form.solarData.roofAreaM2 > 0)
  ) {
    flags.roof_area_from_geospatial = true;
  }
  if (insights?.shadingFactor != null && Number.isFinite(insights.shadingFactor)) {
    flags.shading_from_geospatial = true;
  }
  if (form.billData?.annual_kwh != null && form.billData.annual_kwh > 0) {
    flags.consumption_from_bill = true;
  } else if (form.usageTouched && form.annualKwh > 0) {
    flags.consumption_user_supplied = true;
  }
  if (form.billData?.current_tariff_pence_per_kwh != null) {
    flags.tariff_from_bill = true;
  }

  const epcFlags = form.epcEnrichment?.evidence_flags;
  if (epcFlags?.epc_from_epc_register) {
    flags.epc_from_epc_register = true;
  }
  if (epcFlags?.property_value_from_price_paid) {
    flags.property_value_from_price_paid = true;
  }
  if (epcFlags?.tenure_from_epc_register) {
    flags.tenure_from_epc_register = true;
  }

  return flags;
}

function orientationFactor(roofAzimuth: number, roofTilt: number): number {
  let azimuth = roofAzimuth % 360;
  if (azimuth < 0) azimuth += 360;

  const azimuthDeviation = Math.abs(azimuth - 180);
  const tiltDeviation = Math.abs(roofTilt - 35);
  const azimuthFactor = Math.max(0.72, 1 - azimuthDeviation * 0.002);
  const tiltFactor = Math.max(0.85, 1 - tiltDeviation * 0.005);

  return azimuthFactor * tiltFactor;
}

/**
 * Mirrors ProposalCalculator independence logic for funnel-stage estimates.
 */
export function estimateEnergyIndependencePct(params: {
  systemKw: number;
  batteryKwh: number;
  annualKwh: number;
  irradianceKwhPerKwp: number;
  shadingLoss?: number;
  roofAzimuth?: number;
  roofTilt?: number;
}): number {
  const {
    systemKw,
    batteryKwh,
    annualKwh,
    irradianceKwhPerKwp,
    shadingLoss = 0,
    roofAzimuth = DEFAULT_ROOF_AZIMUTH,
    roofTilt = DEFAULT_ROOF_TILT,
  } = params;

  if (systemKw <= 0 || annualKwh <= 0 || irradianceKwhPerKwp <= 0) {
    return 0;
  }

  const annualGeneration =
    systemKw * irradianceKwhPerKwp * orientationFactor(roofAzimuth, roofTilt) * (1 - shadingLoss);
  const selfUseRate = estimateSelfConsumptionRate(systemKw, batteryKwh);
  const selfUseKwh = Math.min(annualGeneration * selfUseRate, annualKwh);

  const independencePct = Math.min(100, (selfUseKwh / Math.max(1, annualKwh)) * 100);

  return Math.round(independencePct * 10) / 10;
}

/**
 * Mirrors SelfConsumptionRateEstimator — saturating curve of battery days of storage.
 */
export function estimateSelfConsumptionRate(systemKw: number, batteryKwh: number): number {
  if (systemKw <= 0 || batteryKwh <= 0) {
    return SELF_USE_RATE_CURVE_FLOOR;
  }

  const batteryDays = batteryKwh / (systemKw * SELF_USE_RATE_CURVE_IRRADIANCE / 365);

  return SELF_USE_RATE_CURVE_FLOOR
    + (SELF_USE_RATE_CURVE_CAP - SELF_USE_RATE_CURVE_FLOOR)
    * (1 - Math.exp(-SELF_USE_RATE_CURVE_K * batteryDays));
}

export function buildHelioScoreInputFromFunnel(
  form: HelioScoreFunnelFormSlice,
  context: HelioScoreFunnelContext,
): HelioScoreInput | null {
  const postcode = form.postcode.trim();
  if (!postcode) {
    return null;
  }

  const insights = form.solarInsights;
  const roofAzimuth = resolveRoofAzimuth(insights);
  const roofTilt = DEFAULT_ROOF_TILT;
  const irradiance = resolveIrradianceKwhPerKwp(insights, form.solarData, context.heroIrradianceKwhM2);
  const shadingLoss = shadingFactorToLoss(insights?.shadingFactor);
  const roofArea = form.solarData?.roofAreaM2 ?? insights?.roofAreaM2;
  const propertyEvidenceFlags = buildPropertyEvidenceFlags(form, insights);

  const input: HelioScoreInput = {
    postcode,
    address: form.address || undefined,
    roof_azimuth: roofAzimuth,
    roof_tilt: roofTilt,
    irradiance_kwh_per_kwp: irradiance,
    roof_area_m2: roofArea,
    shading_loss: shadingLoss,
    ...(form.epcEnrichment?.epc_rating ? { epc_rating: form.epcEnrichment.epc_rating } : {}),
    ...(form.epcEnrichment?.epc_floor_area_m2 != null
      ? { floor_area_m2: form.epcEnrichment.epc_floor_area_m2 }
      : {}),
    ...(form.epcEnrichment?.property_built_form
      ? { property_type: form.epcEnrichment.property_built_form, property_built_form: form.epcEnrichment.property_built_form }
      : {}),
    ...(form.epcEnrichment?.property_tenure
      ? { property_tenure: form.epcEnrichment.property_tenure }
      : {}),
    ...(form.epcEnrichment?.property_value_mid_gbp != null
      ? { property_value_mid_gbp: form.epcEnrichment.property_value_mid_gbp }
      : {}),
    ...(form.epcEnrichment?.epc_data_source
      ? { epc_data_source: form.epcEnrichment.epc_data_source }
      : {}),
    ...(form.epcEnrichment?.price_paid_data_source
      ? { price_paid_data_source: form.epcEnrichment.price_paid_data_source }
      : {}),
    ...(Object.keys(propertyEvidenceFlags).length > 0
      ? { property_data: propertyEvidenceFlags }
      : {}),
  };

  const hasBillConsumption = (form.billData?.annual_kwh ?? 0) > 0;
  const includeUsageInputs = context.step >= 1 || hasBillConsumption;
  const annualKwh = form.billData?.annual_kwh ?? form.annualKwh;
  const importPence = form.billData?.current_tariff_pence_per_kwh ?? undefined;

  if (includeUsageInputs) {
    input.tariff = {
      import_pence: importPence != null && importPence > 0 ? importPence : DEFAULT_IMPORT_TARIFF_PENCE,
      smart_tariff: (form.selectedTier?.batteryKwh ?? 0) > 0,
    };
  }

  if (includeUsageInputs && annualKwh > 0) {
    input.consumption = { annual_kwh: annualKwh };
  }

  if (
    includeUsageInputs
    && annualKwh > 0
    && irradiance != null
    && irradiance > 0
    && roofArea != null
    && roofArea > 0
    && !form.selectedTier
  ) {
    const indicativeSystemKw = Math.max(1, roofArea * INDICATIVE_SYSTEM_KW_PER_M2);
    const independencePct = estimateEnergyIndependencePct({
      systemKw: indicativeSystemKw,
      batteryKwh: 0,
      annualKwh,
      irradianceKwhPerKwp: irradiance,
      shadingLoss: shadingLoss ?? 0,
      roofAzimuth,
      roofTilt,
    });

    input.consumption = {
      annual_kwh: annualKwh,
      independence_pct: independencePct,
    };
  }

  if (form.selectedTier && annualKwh > 0 && irradiance != null && irradiance > 0) {
    const independencePct = estimateEnergyIndependencePct({
      systemKw: form.selectedTier.systemKw,
      batteryKwh: form.selectedTier.batteryKwh,
      annualKwh,
      irradianceKwhPerKwp: irradiance,
      shadingLoss: shadingLoss ?? 0,
      roofAzimuth,
      roofTilt,
    });

    input.consumption = {
      annual_kwh: annualKwh,
      independence_pct: independencePct,
    };
    input.assets = {
      battery_kwh: form.selectedTier.batteryKwh > 0 ? form.selectedTier.batteryKwh : undefined,
      ev_present: form.selectedTier.includesEv,
    };
    input.tariff = {
      import_pence: importPence != null && importPence > 0 ? importPence : DEFAULT_IMPORT_TARIFF_PENCE,
      smart_tariff: form.selectedTier.batteryKwh > 0,
    };

    const { price, paybackYears, annualSavings } = form.selectedTier;
    if (price != null && price > 0 && paybackYears != null && paybackYears > 0) {
      input.economic = {
        installation_cost_gbp: price,
        payback_years: paybackYears,
        annual_savings_gbp: annualSavings,
      };
    }
  }

  return input;
}
